import { useEffect, useRef, useState } from 'react';
import { TileLayer } from 'react-leaflet';
import { toast } from 'sonner';
import { Layers, Check, TrafficCone } from 'lucide-react';
import { PROVIDER_LAYERS, googleTileUrl, tileUrl } from '../../lib/mapTiles';
import { googleSession, layersFor } from '../../lib/useMapSettings';

function useGoogle(type) {
  const [s, setS] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!type) return undefined;
    let on = true;
    googleSession(type).then((x) => { if (!on) return; if (x) setS(x); else setFailed(true); });
    return () => { on = false; };
  }, [type]);
  return { url: s ? googleTileUrl(s) : null, failed };
}

// Taban katman + otomatik yedeğe geçiş (3 karo hatası ve hiç yüklenen yoksa).
export function BaseTiles({ id, onFail, cfg }) {
  const def = PROVIDER_LAYERS[id];
  const g = useGoogle(def?.google);
  const stat = useRef({ ok: 0, err: 0, done: false });
  useEffect(() => { stat.current = { ok: 0, err: 0, done: false }; }, [id]);
  useEffect(() => { if (g.failed) onFail(); }, [g.failed]); // eslint-disable-line react-hooks/exhaustive-deps
  const url = def?.google ? g.url : def && tileUrl(def, cfg);
  if (!url) return null;
  const handlers = {
    tileload: () => { stat.current.ok += 1; },
    tileerror: () => {
      stat.current.err += 1;
      if (!stat.current.done && stat.current.ok === 0 && stat.current.err >= 3) { stat.current.done = true; onFail(); }
    },
  };
  return (
    <>
      <TileLayer key={id} url={url} attribution={def.attribution} maxZoom={19} eventHandlers={handlers} />
      {(def.overlays || []).map((u) => <TileLayer key={u} url={u} maxZoom={19} />)}
    </>
  );
}

export function TrafficTiles() {
  const g = useGoogle('traffic');
  return g.url ? <TileLayer url={g.url} maxZoom={19} zIndex={5} /> : null;
}

export function useMapLayers(cfg) {
  const layers = layersFor(cfg);
  const [layer, setLayer] = useState(null);
  const [traffic, setTraffic] = useState(false);
  const tried = useRef(new Set());
  useEffect(() => { if (cfg) { setLayer((l) => l || layers[0]); setTraffic(!!cfg.traffic_default && cfg.google_available); } }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = layer || layers[0];
  const onFail = () => {
    const prov = PROVIDER_LAYERS[current]?.provider;
    Object.keys(PROVIDER_LAYERS).forEach((i) => PROVIDER_LAYERS[i].provider === prov && tried.current.add(i));
    tried.current.add(current);
    if (cfg && cfg.fallback === false) { toast.error(`${PROVIDER_LAYERS[current]?.label} haritası yüklenemedi`); return; }
    const all = Object.keys(PROVIDER_LAYERS).filter((i) => !PROVIDER_LAYERS[i].google && PROVIDER_LAYERS[i].provider !== 'carto');
    const next = [...layers, ...all].find((i) => !tried.current.has(i));
    if (next) { toast.message(`${PROVIDER_LAYERS[current]?.label} yüklenemedi, ${PROVIDER_LAYERS[next].label} kullanılıyor`); setLayer(next); }
  };
  return { layers, layer: current, setLayer, traffic, setTraffic, onFail, trafficAvailable: !!cfg?.google_available && (cfg?.providers || []).includes('google') };
}

export function LayerMenu({ m }) {
  const [open, setOpen] = useState(false);
  if (m.layers.length < 2 && !m.trafficAvailable) return null;
  return (
    <div className="relative" data-testid="ss-map-layer-menu">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="ss-map-layer-btn"
        className="h-8 px-2.5 inline-flex items-center gap-1 text-xs font-semibold rounded-lg shadow-md border bg-white text-slate-700 border-slate-200 hover:bg-slate-50">
        <Layers className="h-3.5 w-3.5" />{PROVIDER_LAYERS[m.layer]?.label}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-xs">
          {m.layers.map((id) => (
            <button key={id} type="button" onClick={() => { m.setLayer(id); setOpen(false); }} data-testid={`ss-map-layer-${id.replace(':', '-')}`}
              className="w-full px-3 py-1.5 flex items-center justify-between hover:bg-slate-50 text-left text-slate-700">
              {PROVIDER_LAYERS[id].label}{m.layer === id && <Check className="h-3.5 w-3.5 text-emerald-600" />}
            </button>
          ))}
          {m.trafficAvailable && (
            <button type="button" onClick={() => m.setTraffic((t) => !t)} data-testid="ss-map-traffic-toggle"
              className="w-full px-3 py-1.5 mt-1 border-t border-slate-100 flex items-center justify-between hover:bg-slate-50 text-left text-slate-700">
              <span className="inline-flex items-center gap-1"><TrafficCone className="h-3.5 w-3.5" />Trafik</span>{m.traffic && <Check className="h-3.5 w-3.5 text-emerald-600" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
